import { useMemo, useCallback } from 'react';
import type { ChatWidgetProps, Message, RichContent, ChatAction } from '../types';

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeLocalMessageFlows(
  flows: ChatWidgetProps['localMessageFlows']
): NonNullable<ChatWidgetProps['localMessageFlows']> {
  if (!isPlainRecord(flows)) return {};

  const normalized: NonNullable<ChatWidgetProps['localMessageFlows']> = {};

  for (const [key, value] of Object.entries(flows)) {
    if (!isPlainRecord(value)) continue;

    const userMessage =
      typeof value.userMessage === 'string' && value.userMessage.trim()
        ? value.userMessage.trim()
        : undefined;
    const reply = value.reply;
    if (!isPlainRecord(reply)) continue;

    const text = typeof reply.text === 'string' ? reply.text.trim() : '';
    if (!text) continue;

    normalized[key] = {
      ...(userMessage ? { userMessage } : {}),
      reply: {
        text,
        ...(typeof reply.type === 'string' ? { type: reply.type as Message['type'] } : {}),
        ...(isPlainRecord(reply.content) ? { content: reply.content as RichContent } : {}),
      },
    };
  }

  return normalized;
}

/** Validated host-defined flows and local transcript messages, without a network send. */
export function useLocalMessages(
  flows: ChatWidgetProps['localMessageFlows'],
  dispatch: (action: ChatAction) => void
) {
  const localMessageFlows = useMemo(() => normalizeLocalMessageFlows(flows), [flows]);

  const addLocalMessage = useCallback(
    (
      sender: Message['sender'],
      payload: {
        text: string;
        type?: Message['type'];
        content?: RichContent;
        localOnly?: boolean;
      },
      status: Message['status']
    ) => {
      const now = new Date();
      dispatch({
        type: 'ADD_MESSAGE',
        message: {
          id: `local-${sender}-${now.getTime()}-${Math.random().toString(36).slice(2, 8)}`,
          sender,
          text: payload.text,
          timestamp: now.toISOString(),
          status,
          ...(payload.localOnly ? { localOnly: true } : null),
          ...(payload.type ? { type: payload.type } : null),
          ...(payload.content ? { richContent: payload.content } : null),
        },
      });
    },
    [dispatch]
  );

  return { localMessageFlows, addLocalMessage };
}
